export const MAX_RESULT_PDF_BYTES = 4 * 1024 * 1024;
export const normalizePerformerName = value => String(value || '').normalize('NFC').replace(/\s+/g, '').toLowerCase();
export const resultRecipientKey = recipient => `${recipient.registrantId}:${recipient.performerIndex}`;
const matchesPerformerPdf = (filename, recipient) => {
    const basename = filename.slice(0, -4);
    const name = normalizePerformerName(recipient.name);
    return normalizePerformerName(basename) === name || (recipient.performanceCategory === 'Ensemble'
        && basename.split('&').some(part => normalizePerformerName(part) === name));
};

const savedAwards = ['Silver', 'Gold', 'Diamond', 'Sapphire', 'Fail'];

export function requireSyncedResultRows(rows) {
    if (rows.some(row => row.isFinalized && (row.needsAwardSync
        || !savedAwards.includes(row.registrant?.finalAward)
        || typeof row.registrant?.averageScore !== 'number' || !Number.isFinite(row.registrant.averageScore)))) {
        throw new Error('Saved results are missing or need syncing. Run Sync Awards, refresh Scoring Recap, and reopen the email preview.');
    }
}

export const eligibleResultRows = (rows, kind) => rows.filter(row => row.isFinalized
    && !row.needsAwardSync
    && (kind === 'winner' ? savedAwards.slice(0, 4).includes(row.registrant?.finalAward)
        : row.registrant?.finalAward === 'Fail'));

export function matchCommentSheets(recipients, files) {
    const pdfs = Array.from(files || []).filter(file => /\.pdf$/i.test(file.name));
    const nameCounts = new Map();
    recipients.forEach(recipient => {
        const name = normalizePerformerName(recipient.name);
        nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    });
    return recipients.map(recipient => {
        const name = normalizePerformerName(recipient.name);
        const matches = pdfs.filter(file => matchesPerformerPdf(file.name, recipient));
        const problem = recipient.problem || (nameCounts.get(name) > 1 ? 'Duplicate performer name; PDF mapping is ambiguous'
            : matches.length === 0 ? 'No matching PDF'
                : matches.length > 1 ? 'Multiple matching PDFs'
                    : matches[0].size > MAX_RESULT_PDF_BYTES ? 'PDF exceeds 4 MB' : '');
        return { ...recipient, attachment: matches.length === 1 ? matches[0] : null, problem };
    });
}

export function matchNonQualifierAttachments(recipients, commentFiles, certificateFiles) {
    const comments = matchCommentSheets(recipients, commentFiles);
    const certificates = matchCommentSheets(recipients, certificateFiles);
    return comments.map((recipient, index) => ({
        ...recipient,
        certificateAttachment: certificates[index].attachment,
        problem: recipients[index].problem || (recipient.problem ? `Comment sheet: ${recipient.problem}`
            : certificates[index].problem ? `E-certificate: ${certificates[index].problem}` : ''),
    }));
}

export function singleNonQualifierProblem(recipient, commentFile, certificateFile) {
    const commentProblem = singleCommentSheetProblem(recipient, commentFile);
    if (commentProblem) return commentProblem.startsWith('Choose') ? `Comment sheet: ${commentProblem}` : commentProblem;
    const certificateProblem = singleCommentSheetProblem(recipient, certificateFile);
    return certificateProblem ? `E-certificate: ${certificateProblem}` : '';
}

export function singleCommentSheetProblem(recipient, file) {
    if (!recipient) return 'Performer is no longer in this preview. Reopen it.';
    if (recipient.problem) return recipient.problem;
    if (!file) return 'Choose a PDF for this performer.';
    if (!/\.pdf$/i.test(file.name)) return 'Choose a PDF file.';
    if (file.size > MAX_RESULT_PDF_BYTES) return 'PDF exceeds 4 MB';
    if (!matchesPerformerPdf(file.name, recipient)) {
        return 'PDF filename must exactly match this performer’s name or list their full ensemble name separated by &.';
    }
    return '';
}

export function readResultPdf(file) {
    if (!file || !/\.pdf$/i.test(file.name) || file.size > MAX_RESULT_PDF_BYTES) {
        return Promise.reject(new Error('Choose a PDF file of at most 4 MB.'));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
        reader.onload = () => {
            const base64 = String(reader.result).split(',')[1];
            if (!base64?.startsWith('JVBERi0')) {
                reject(new Error(`${file.name} is not a PDF file.`));
                return;
            }
            if (!atob(base64.slice(-2740)).includes('%%EOF')) {
                reject(new Error(`${file.name} has no PDF end marker.`));
                return;
            }
            resolve({ filename: file.name, base64 });
        };
        reader.readAsDataURL(file);
    });
}

export function validResultDates(dates) {
    return ['confirmationDeadline', 'rundownReleaseDate'].every(key => typeof dates[key] === 'string'
        && dates[key].trim() && dates[key].length <= 160 && !/[[\]\r\n]/.test(dates[key]));
}
